/* assets/cascade-menu.js
 * <cascade-menu> custom element. Drives ARIA state and intent-aware
 * open/close timing for a 4-level cascading header flyout. Visibility
 * itself is CSS-driven via :focus-within / .is-open and [aria-expanded]
 * selectors.
 *
 * UX patches (per https://www.smashingmagazine.com/2021/05/
 * frustrating-design-patterns-mega-dropdown-hover-menus/):
 *  - OPEN_DELAY: short delay before opening on hover so passing the
 *    cursor across the nav doesn't open every menu.
 *  - CLOSE_DELAY: longer delay before closing so brief mouse-leaves
 *    (arcing toward column 2/3) don't kill the panel.
 *  - SWITCH_DELAY: small delay before switching the active branch so
 *    diagonal travel from a parent toward its submenu doesn't snap
 *    onto a sibling.
 *  - Keyboard focus opens instantly via the focusin handler.
 */

if (!customElements.get('cascade-menu')) {
  const OPEN_DELAY = 120;
  const CLOSE_DELAY = 300;
  const SWITCH_DELAY = 80;

  class CascadeMenu extends HTMLElement {
    constructor() {
      super();
      this._openTimer = null;
      this._closeTimer = null;
      this._branchTimers = new WeakMap();

      this._onMouseEnter = this._onMouseEnter.bind(this);
      this._onMouseLeave = this._onMouseLeave.bind(this);
      this._onKeyDown = this._onKeyDown.bind(this);
      this._onResize = this._onResize.bind(this);
    }

    connectedCallback() {
      this.trigger = this.querySelector('.cascade-menu__trigger');
      this.panel = this.querySelector('.cascade-menu__panel');
      if (!this.trigger) return;

      this._setDirection();
      window.addEventListener('resize', this._onResize, { passive: true });

      this.addEventListener('mouseenter', this._onMouseEnter);
      this.addEventListener('mouseleave', this._onMouseLeave);
      this.addEventListener('keydown', this._onKeyDown);

      // Open instantly when keyboard focus enters anywhere in the menu.
      this.addEventListener('focusin', () => {
        this._clearOpenTimer();
        this._clearCloseTimer();
        this._setDirection();
        this._open();
      });

      // Close when focus leaves the whole component.
      this.addEventListener('focusout', (event) => {
        if (!this.contains(event.relatedTarget)) {
          this._closeAll();
        }
      });

      this.querySelectorAll('.cascade-menu__item--has-children > .cascade-menu__link').forEach((link) => {
        link.addEventListener('mouseenter', () => this._scheduleBranchOpen(link));
        link.addEventListener('mouseleave', () => this._cancelBranchTimer(link));
        link.addEventListener('focus', () => this._openBranch(link));
      });

      // Hovering a sibling without children should still close any open
      // branch at the same level — but with the same switch delay so a
      // diagonal mouse path isn't punished.
      this.querySelectorAll('.cascade-menu__level > .cascade-menu__item:not(.cascade-menu__item--has-children) > .cascade-menu__link').forEach((link) => {
        link.addEventListener('mouseenter', () => this._scheduleBranchClose(link));
        link.addEventListener('mouseleave', () => this._cancelBranchTimer(link));
      });
    }

    disconnectedCallback() {
      window.removeEventListener('resize', this._onResize);
      this._clearOpenTimer();
      this._clearCloseTimer();
    }

    _onResize() {
      this._setDirection();
    }

    _setDirection() {
      const myRect = this.getBoundingClientRect();
      if (!myRect.width) return;

      const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
      const rootFont = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
      const colRem = parseFloat(getComputedStyle(this).getPropertyValue('--cascade-col-width')) || 22;
      const fullPanelWidth = colRem * rootFont * 3;

      const wouldOverflowRight = myRect.left + fullPanelWidth > viewportWidth;
      const fitsOnLeft = myRect.right - fullPanelWidth >= 0;

      if (wouldOverflowRight && fitsOnLeft) {
        this.dataset.direction = 'start';
      } else {
        this.dataset.direction = 'end';
      }
    }

    /* ---------- Panel open/close (with intent delays) ---------- */

    _onMouseEnter() {
      this._clearCloseTimer();
      if (this.classList.contains('is-open')) return;
      this._openTimer = window.setTimeout(() => {
        this._setDirection();
        this._open();
      }, OPEN_DELAY);
    }

    _onMouseLeave() {
      this._clearOpenTimer();
      this._closeTimer = window.setTimeout(() => this._closeAll(), CLOSE_DELAY);
    }

    _open() {
      this.classList.add('is-open');
      if (this.trigger) this.trigger.setAttribute('aria-expanded', 'true');
    }

    _clearOpenTimer() {
      if (this._openTimer) {
        clearTimeout(this._openTimer);
        this._openTimer = null;
      }
    }

    _clearCloseTimer() {
      if (this._closeTimer) {
        clearTimeout(this._closeTimer);
        this._closeTimer = null;
      }
    }

    /* ---------- Branch switching (with switch delay) ---------- */

    _scheduleBranchOpen(link) {
      this._cancelBranchTimer(link);
      const timer = window.setTimeout(() => this._openBranch(link), SWITCH_DELAY);
      this._branchTimers.set(link, timer);
    }

    _scheduleBranchClose(link) {
      this._cancelBranchTimer(link);
      const timer = window.setTimeout(() => this._closeSiblings(link), SWITCH_DELAY);
      this._branchTimers.set(link, timer);
    }

    _cancelBranchTimer(link) {
      const timer = this._branchTimers.get(link);
      if (timer) {
        clearTimeout(timer);
        this._branchTimers.delete(link);
      }
    }

    _openBranch(link) {
      this._closeSiblings(link);
      link.setAttribute('aria-expanded', 'true');
    }

    _closeSiblings(link) {
      const list = link.closest('.cascade-menu__level');
      if (!list) return;
      list.querySelectorAll(':scope > .cascade-menu__item > .cascade-menu__link[aria-expanded="true"]').forEach((sib) => {
        if (sib !== link) {
          sib.setAttribute('aria-expanded', 'false');
          sib.parentElement.querySelectorAll('.cascade-menu__link[aria-expanded="true"]').forEach((deeper) => {
            deeper.setAttribute('aria-expanded', 'false');
          });
        }
      });
    }

    _onKeyDown(event) {
      if (event.key === 'Escape') {
        this._closeAll();
        if (this.trigger) this.trigger.focus();
      }
    }

    _closeAll() {
      this._clearOpenTimer();
      this._clearCloseTimer();
      this.querySelectorAll('.cascade-menu__link[aria-expanded="true"]').forEach((l) => l.setAttribute('aria-expanded', 'false'));
      this.classList.remove('is-open');
      if (this.trigger) this.trigger.setAttribute('aria-expanded', 'false');
    }
  }

  customElements.define('cascade-menu', CascadeMenu);
}
